import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: "#000000",
      light: "#2A2A2A",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFFFFF",
      light: "#FFFFFF",
      dark: "#F0F0F0",
      contrastText: "#000000",
    },
    background: {
      default: "#FFFFFF",
      paper: "#F8F9FA",
    },
    text: {
      primary: "#000000",
      secondary: "#404040",
      disabled: "#A0A0A0",
    },
    grey: {
      50: "#F9F9F9",
      100: "#F2F2F2",
      200: "#E8E8E8",
      300: "#D8D8D8",
      400: "#B8B8B8",
      500: "#999999",
      600: "#707070",
      700: "#555555",
      800: "#333333",
      900: "#1A1A1A",
    },
    divider: "#E8E8E8",
    action: {
      active: "#000000",
      hover: "rgba(0, 0, 0, 0.04)",
      selected: "rgba(0, 0, 0, 0.08)",
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: "3.5rem",
      letterSpacing: "-0.75px",
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.75rem",
      letterSpacing: "-0.5px",
      lineHeight: 1.2,
      position: "relative",
      display: "inline-block",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: "-12px",
        left: 0,
        width: "80px",
        height: "4px",
        backgroundColor: "#000000",
        borderRadius: "2px",
      },
    },
    h3: {
      fontWeight: 700,
      fontSize: "2.25rem",
      letterSpacing: "-0.25px",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.75rem",
      letterSpacing: "-0.1px",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      textTransform: "uppercase",
      letterSpacing: "1.5px",
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1.125rem",
      lineHeight: 1.6,
      color: "#404040",
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
    },
    body1: {
      fontSize: "1.0625rem",
      lineHeight: 1.7,
      color: "#404040",
    },
    body2: {
      fontSize: "0.9375rem",
      lineHeight: 1.6,
      color: "#707070",
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      letterSpacing: "1.25px",
      textTransform: "uppercase",
    },
    caption: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      letterSpacing: "0.4px",
      color: "#707070",
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "1.5px",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 3px rgba(0,0,0,0.05)",
    "0 3px 6px rgba(0,0,0,0.08)",
    "0 6px 12px rgba(0,0,0,0.10)",
    "0 12px 24px rgba(0,0,0,0.12)",
    "0 18px 36px rgba(0,0,0,0.14)",
    "0 24px 48px rgba(0,0,0,0.16)",
    "0 30px 60px rgba(0,0,0,0.18)",
    "0 36px 72px rgba(0,0,0,0.20)",
    "0 42px 84px rgba(0,0,0,0.22)",
    "0 48px 96px rgba(0,0,0,0.24)",
    "0 54px 108px rgba(0,0,0,0.26)",
    "0 60px 120px rgba(0,0,0,0.28)",
    "0 66px 132px rgba(0,0,0,0.30)",
    "0 72px 144px rgba(0,0,0,0.32)",
    "0 78px 156px rgba(0,0,0,0.34)",
    "0 84px 168px rgba(0,0,0,0.36)",
    "0 90px 180px rgba(0,0,0,0.38)",
    "0 96px 192px rgba(0,0,0,0.40)",
    "0 102px 204px rgba(0,0,0,0.42)",
    "0 108px 216px rgba(0,0,0,0.44)",
    "0 114px 228px rgba(0,0,0,0.46)",
    "0 120px 240px rgba(0,0,0,0.48)",
    "0 126px 252px rgba(0,0,0,0.50)",
    "0 132px 264px rgba(0,0,0,0.52)",
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: "smooth",
        },
        body: {
          fontFeatureSettings: '"cv11", "ss01"',
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "::-webkit-scrollbar": {
          width: "10px",
        },
        "::-webkit-scrollbar-track": {
          background: "#F2F2F2",
        },
        "::-webkit-scrollbar-thumb": {
          background: "#D8D8D8",
          borderRadius: "5px",
          "&:hover": {
            background: "#B8B8B8",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          padding: "14px 32px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "48px",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        },
        contained: {
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        },
        outlined: {
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
        sizeSmall: {
          padding: "8px 20px",
          minHeight: "36px",
          fontSize: "0.8125rem",
        },
        sizeLarge: {
          padding: "16px 40px",
          minHeight: "56px",
          fontSize: "1rem",
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #E8E8E8",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
            borderColor: "#D8D8D8",
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px",
          "&:last-child": {
            paddingBottom: "24px",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderBottom: "1px solid #E8E8E8",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid #E8E8E8",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        elevation1: {
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        },
        elevation2: {
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        },
        elevation3: {
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#E8E8E8",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B8B8B8",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#000000",
              borderWidth: "2px",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          "&.Mui-focused": {
            color: "#000000",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          margin: "4px 8px",
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: "6px",
          "&.MuiChip-outlined": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          alignItems: "center",
        },
        standardSuccess: {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          color: "#000000",
          borderLeft: "4px solid #000000",
        },
        standardInfo: {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          color: "#000000",
          borderLeft: "4px solid #707070",
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: "24px 0",
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: "#E8E8E8",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontWeight: 500,
          "&.Mui-active": {
            fontWeight: 600,
            color: "#000000",
          },
          "&.Mui-completed": {
            fontWeight: 600,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          height: "6px",
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: "#000000",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: "2px solid #F2F2F2",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#000000",
          fontSize: "0.8125rem",
          fontWeight: 500,
          padding: "8px 12px",
          borderRadius: "6px",
        },
        arrow: {
          color: "#000000",
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(4px)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          "& .MuiAlert-root": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: "3px",
          borderRadius: "3px 3px 0 0",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          textTransform: "none",
          fontSize: "1rem",
          "&.Mui-selected": {
            fontWeight: 600,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          borderWidth: "1.5px",
          fontWeight: 500,
          "&.Mui-selected": {
            backgroundColor: "#000000",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#1A1A1A",
            },
          },
        },
      },
    },
  },
});

export default theme;